import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaUserPlus,
  FaSearch,
  FaSync,
  FaUsers,
  FaUserTie,
  FaUmbrellaBeach,
  FaUserClock,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaIdBadge,
  FaFileExcel,
  FaFileAlt,
  FaFilePdf,
  FaChartPie,
  FaClipboardList,
  FaBirthdayCake,
  FaBan,
  FaCalendarCheck,
  FaDownload,
  FaFileImport,
} from "react-icons/fa";
import { read as readXLSX, utils as XLSXUtils, writeFile as writeXLSXFile } from "xlsx";
import Modal from "../components/Modal";
import HumanResourcesService from "../services/HumanResourcesService";
import { formatDateOnly, normalizeDateInput } from "../utils/dateOnly";
import { useAuth } from "../AuthContext";
import { hasPermission, HUMAN_RESOURCES_MANAGE_PERMISSION } from "../utils/permissions";

const STATUS_OPTIONS = [
  { value: "ALL", label: "Todos os status" },
  { value: "ACTIVE", label: "Ativo" },
  { value: "ON_LEAVE", label: "Licença" },
  { value: "VACATION", label: "Férias" },
  { value: "INACTIVE", label: "Inativo" },
  { value: "TERMINATED", label: "Desligado" },
];

const STATUS_LABELS = {
  ACTIVE: "Ativo",
  ON_LEAVE: "Licença",
  VACATION: "Férias",
  INACTIVE: "Inativo",
  TERMINATED: "Desligado",
};

const STATUS_STYLES = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  ON_LEAVE: "bg-amber-100 text-amber-700",
  VACATION: "bg-sky-100 text-sky-700",
  INACTIVE: "bg-slate-100 text-slate-600",
  TERMINATED: "bg-rose-100 text-rose-700",
};

const TAB_ITEMS = [
  { id: "overview", label: "Painel", icon: FaChartPie },
  { id: "vacationControl", label: "Controle de férias", icon: FaUmbrellaBeach },
  { id: "employeeHistory", label: "Histórico", icon: FaClipboardList },
];

const HISTORY_TYPE_OPTIONS = [
  { value: "LEAVE", label: "Licença" },
  { value: "VACATION", label: "Férias" },
  { value: "OCCURRENCE", label: "Ocorrências" },
  { value: "TERMINATION", label: "Desligamento" },
];

const HISTORY_TYPE_LABELS = {
  LEAVE: "Licença",
  VACATION: "Férias",
  OCCURRENCE: "Ocorrência",
  TERMINATION: "Desligamento",
};

const EMPLOYEE_IMPORT_COLUMNS = [
  { key: "registrationNumber", label: "Matrícula", required: false, instructions: "Código interno de identificação." },
  { key: "fullName", label: "Nome completo", required: true, instructions: "Nome do colaborador." },
  { key: "email", label: "E-mail", required: false, instructions: "E-mail corporativo." },
  { key: "phone", label: "Telefone", required: false, instructions: "Telefone principal." },
  { key: "department", label: "Departamento", required: false, instructions: "Ex: Financeiro, RH, Operações." },
  { key: "position", label: "Cargo", required: false, instructions: "Cargo atual do colaborador." },
  { key: "employmentType", label: "Tipo de vínculo", required: false, instructions: "CLT, PJ, Estágio, Temporário, Terceirizado, Aprendiz ou Autônomo." },
  { key: "hireDate", label: "Data de admissão", required: false, instructions: "Formato AAAA-MM-DD." },
  { key: "salary", label: "Salário", required: false, instructions: "Valor numérico. Exemplo: 3500.50." },
  { key: "vacationDays", label: "Saldo de férias", required: false, instructions: "Quantidade de dias disponível." },
  { key: "status", label: "Status", required: true, instructions: "Ativo, Licença, Férias, Inativo ou Desligado." },
  { key: "notes", label: "Observações", required: false, instructions: "Informações adicionais." },
];

const HISTORY_IMPORT_COLUMNS = [
  { key: "registrationNumber", label: "Matrícula do colaborador", required: false, instructions: "Preferencial para localizar o colaborador." },
  { key: "employeeEmail", label: "E-mail do colaborador", required: false, instructions: "Alternativa para localizar o colaborador." },
  { key: "employeeName", label: "Nome do colaborador", required: false, instructions: "Alternativa quando matrícula/e-mail não forem informados." },
  { key: "type", label: "Tipo de histórico", required: true, instructions: "Licença, Férias, Ocorrências ou Desligamento." },
  { key: "startDate", label: "Data de início", required: false, instructions: "Formato AAAA-MM-DD." },
  { key: "endDate", label: "Data de fim", required: false, instructions: "Formato AAAA-MM-DD." },
  { key: "days", label: "Dias", required: false, instructions: "Obrigatório para tipo Férias." },
  { key: "notes", label: "Observações", required: false, instructions: "Detalhes complementares." },
];

const getImportColumnLabel = (column) => `${column.required ? "* " : ""}${column.label}`;
const normalizeImportHeader = (header) =>
  String(header || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\*/g, "")
    .toLowerCase()
    .trim();
const normalizeImportText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const coerceImportValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
};

const EMPLOYMENT_TYPES = [
  "CLT",
  "PJ",
  "Estágio",
  "Temporário",
  "Terceirizado",
  "Aprendiz",
  "Autônomo",
];

const defaultFilters = {
  search: "",
  status: "ALL",
  department: "",
  employmentType: "ALL",
};

const defaultFormState = {
  registrationNumber: "",
  fullName: "",
  socialName: "",
  email: "",
  phone: "",
  cpf: "",
  rg: "",
  birthDate: "",
  gender: "",
  maritalStatus: "",
  department: "",
  position: "",
  employmentType: "",
  hireDate: "",
  terminationDate: "",
  workShift: "",
  salary: "",
  vacationDays: "",
  notes: "",
  addressStreet: "",
  addressNumber: "",
  addressComplement: "",
  addressNeighborhood: "",
  addressCity: "",
  addressState: "",
  addressZipCode: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  status: "ACTIVE",
  userId: "",
  vacationDelegateUserIds: [],
};

const defaultHistoryForm = {
  id: null,
  employeeId: "",
  type: "VACATION",
  startDate: "",
  endDate: "",
  days: "",
  notes: "",
};

const defaultOverview = {
  summary: {
    total: 0,
    active: 0,
    vacation: 0,
    onLeave: 0,
    inactive: 0,
    terminated: 0,
    withSystemAccess: 0,
    withoutSystemAccess: 0,
  },
  headcountByDepartment: [],
  employmentTypeDistribution: [],
  recentHires: [],
  upcomingBirthdays: [],
  vacationSchedule: [],
  withoutSystemAccess: [],
};

const createOverviewFallback = () => ({
  summary: { ...defaultOverview.summary },
  headcountByDepartment: [],
  employmentTypeDistribution: [],
  recentHires: [],
  upcomingBirthdays: [],
  vacationSchedule: [],
  withoutSystemAccess: [],
});

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return numeric.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatDepartment = (department, position) => {
  if (!department && !position) {
    return "—";
  }
  if (!department) {
    return position;
  }
  if (!position) {
    return department;
  }
  return `${department} • ${position}`;
};

const formatEmploymentType = (value) => {
  if (!value) {
    return "—";
  }
  return value;
};

const formatDateRange = (start, end) => {
  if (!start && !end) {
    return "—";
  }
  if (start && end) {
    return `${formatDateOnly(start)} até ${formatDateOnly(end)}`;
  }
  if (start) {
    return `A partir de ${formatDateOnly(start)}`;
  }
  return `Até ${formatDateOnly(end)}`;
};

const formatDaysLabel = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  if (numeric === 0) return "0 dias";
  if (numeric === 1) return "1 dia";
  return `${numeric} dias`;
};

const calculateVacationEndDate = (startDate, days) => {
  if (!startDate || !days) {
    return "";
  }
  const totalDays = Number(days);
  if (!Number.isFinite(totalDays) || totalDays <= 0) {
    return "";
  }
  const date = new Date(`${startDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + totalDays - 1);
  return date.toISOString().slice(0, 10);
};

export default function HumanResources() {
  const { user } = useAuth();
  const canManage = useMemo(
    () => hasPermission(user, HUMAN_RESOURCES_MANAGE_PERMISSION),
    [user]
  );

  const [filters, setFilters] = useState({ ...defaultFilters });
  const [filterForm, setFilterForm] = useState({ ...defaultFilters });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ ...defaultFormState });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [overview, setOverview] = useState(defaultOverview);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");
  const [vacationControl, setVacationControl] = useState([]);
  const [vacationControlLoading, setVacationControlLoading] = useState(true);
  const [vacationControlError, setVacationControlError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportError, setReportError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyForm, setHistoryForm] = useState({ ...defaultHistoryForm });
  const [historySaving, setHistorySaving] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyEmployeeSearch, setHistoryEmployeeSearch] = useState("");
  const [activeEmployeeOptions, setActiveEmployeeOptions] = useState([]);
  const [vacationEditModalOpen, setVacationEditModalOpen] = useState(false);
  const [vacationEditSaving, setVacationEditSaving] = useState(false);
  const [vacationEditError, setVacationEditError] = useState("");
  const [vacationEditForm, setVacationEditForm] = useState({
    employeeId: "",
    vacationDays: "",
    acquisitionPeriodStart: "",
    acquisitionPeriodEnd: "",
  });
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importType, setImportType] = useState("EMPLOYEES");
  const [importingData, setImportingData] = useState(false);
  const [importError, setImportError] = useState("");
  const [importSummary, setImportSummary] = useState("");

  const requestFilters = useMemo(
    () => ({
      search: filters.search || undefined,
      status:
        filters.status && filters.status !== "ALL" ? filters.status : undefined,
      department: filters.department || undefined,
      employmentType:
        filters.employmentType && filters.employmentType !== "ALL"
          ? filters.employmentType
          : undefined,
    }),
    [filters]
  );

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await HumanResourcesService.listEmployees(requestFilters);
      setEmployees(list);
    } catch (err) {
      const message =
        err?.response?.data?.error || "Não foi possível carregar os colaboradores.";
      setEmployees([]);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [requestFilters]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const data = await HumanResourcesService.getOverview();
      if (data && typeof data === "object") {
        setOverview(data);
      } else {
        setOverview(createOverviewFallback());
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || "Não foi possível carregar os indicadores de RH.";
      setOverview(createOverviewFallback());
      setOverviewError(message);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadVacationControl = useCallback(async () => {
    setVacationControlLoading(true);
    setVacationControlError("");
    try {
      const list = await HumanResourcesService.listVacationControl();
      setVacationControl(list);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        "Não foi possível carregar o controle de férias.";
      setVacationControl([]);
      setVacationControlError(message);
    } finally {
      setVacationControlLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVacationControl();
  }, [loadVacationControl]);

  const loadEmployeeHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const list = await HumanResourcesService.listEmployeeHistory();
      setEmployeeHistory(list);
    } catch (err) {
      setEmployeeHistory([]);
      setError(err?.response?.data?.error || "Não foi possível carregar o histórico.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployeeHistory();
  }, [loadEmployeeHistory]);

  const loadActiveEmployeesForHistory = useCallback(async () => {
    try {
      const activeList = await HumanResourcesService.listEmployees({ status: "ACTIVE" });
      setActiveEmployeeOptions(activeList);
    } catch (err) {
      setActiveEmployeeOptions([]);
    }
  }, []);

  const departmentSuggestions = useMemo(() => {
    const suggestionSet = new Set();
    employees.forEach((employee) => {
      if (employee.department) {
        suggestionSet.add(employee.department);
      }
    });
    return Array.from(suggestionSet).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [employees]);

  const delegateOptions = useMemo(() => {
    const currentUserId = form.userId ? String(form.userId) : "";
    return [...availableUsers]
      .filter((option) => String(option.id) !== currentUserId)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [availableUsers, form.userId]);

  const filteredActiveEmployeeOptions = useMemo(() => {
    const query = historyEmployeeSearch.trim().toLowerCase();
    const sorted = [...activeEmployeeOptions].sort((a, b) =>
      (a.fullName || "").localeCompare(b.fullName || "", "pt-BR")
    );
    if (!query) {
      return sorted;
    }
    return sorted.filter((employee) =>
      (employee.fullName || "").toLowerCase().includes(query)
    );
  }, [activeEmployeeOptions, historyEmployeeSearch]);

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...defaultFormState });
    setFormError("");
    setAvailableUsers([]);
  };

  const loadAssignableUsers = useCallback(async () => {
    if (!canManage) {
      return;
    }
    setUsersLoading(true);
    try {
      const users = await HumanResourcesService.getAvailableUsers();
      setAvailableUsers(users);
    } catch (err) {
      console.error("Falha ao carregar usuários disponíveis", err);
      setAvailableUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [canManage]);

  const downloadEmployeesCsv = useCallback(
    async (params = {}, options = {}) => {
      const { closeModal = false, onError } = options;
      setExporting(true);
      try {
        const { blob, fileName } = await HumanResourcesService.downloadEmployeesReport(
          params
        );
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "relatorio-colaboradores.csv";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
        if (closeModal) {
          setReportModalOpen(false);
        }
      } catch (err) {
        const message =
          err?.response?.data?.error || "Não foi possível gerar o relatório.";
        if (typeof onError === "function") {
          onError(message);
        } else {
          setError(message);
        }
      } finally {
        setExporting(false);
      }
    },
    [setError, setReportModalOpen]
  );

  const downloadEmployeesPdf = useCallback(
    async (params = {}, options = {}) => {
      const { closeModal = false, onError } = options;
      setDownloadingPdf(true);
      try {
        const { blob, fileName } = await HumanResourcesService.downloadEmployeesPdf(params);
        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "relatorio-colaboradores.pdf";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
        if (closeModal) {
          setReportModalOpen(false);
        }
      } catch (err) {
        const message =
          err?.response?.data?.error || "Não foi possível gerar o PDF de colaboradores.";
        if (typeof onError === "function") {
          onError(message);
        } else {
          setError(message);
        }
      } finally {
        setDownloadingPdf(false);
      }
    },
    [setError, setReportModalOpen]
  );

  const handleReportDownload = useCallback(
    async (params = {}) => {
      setReportError("");
      await downloadEmployeesCsv(params, { onError: setReportError });
    },
    [downloadEmployeesCsv]
  );

  const handleExportCurrentFilters = useCallback(async () => {
    setError("");
    await downloadEmployeesCsv(requestFilters, { onError: setError });
  }, [downloadEmployeesCsv, requestFilters]);

  const handleDownloadPdf = useCallback(async () => {
    setError("");
    await downloadEmployeesPdf(requestFilters, { onError: setError });
  }, [downloadEmployeesPdf, requestFilters]);

  const handleRefresh = useCallback(() => {
    loadEmployees();
    loadOverview();
    loadVacationControl();
    loadEmployeeHistory();
  }, [loadEmployees, loadOverview, loadVacationControl, loadEmployeeHistory]);

  const openReportModal = () => {
    setReportError("");
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    if (exporting || downloadingPdf) {
      return;
    }
    setReportModalOpen(false);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...defaultFormState });
    setFormError("");
    setModalOpen(true);
    if (canManage) {
      loadAssignableUsers();
    }
  };

  const openHistoryModal = () => {
    setHistoryError("");
    setHistoryForm({ ...defaultHistoryForm });
    setHistoryEmployeeSearch("");
    setHistoryModalOpen(true);
    loadActiveEmployeesForHistory();
  };

  const openImportModal = () => {
    setImportError("");
    setImportSummary("");
    setImportType("EMPLOYEES");
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (importingData) return;
    setImportModalOpen(false);
    setImportError("");
    setImportSummary("");
  };

  const closeHistoryModal = () => {
    if (historySaving) return;
    setHistoryModalOpen(false);
    setHistoryError("");
    setHistoryEmployeeSearch("");
    setHistoryForm({ ...defaultHistoryForm });
  };

  const openEditModal = (employee) => {
    setEditingId(employee.id);
    setForm({
      registrationNumber: employee.registrationNumber || "",
      fullName: employee.fullName || "",
      socialName: employee.socialName || "",
      email: employee.email || "",
      phone: employee.phone || "",
      cpf: employee.cpf || "",
      rg: employee.rg || "",
      birthDate: normalizeDateInput(employee.birthDate) || "",
      gender: employee.gender || "",
      maritalStatus: employee.maritalStatus || "",
      department: employee.department || "",
      position: employee.position || "",
      employmentType: employee.employmentType || "",
      hireDate: normalizeDateInput(employee.hireDate) || "",
      terminationDate: normalizeDateInput(employee.terminationDate) || "",
      workShift: employee.workShift || "",
      salary:
        employee.salary === null || employee.salary === undefined
          ? ""
          : String(employee.salary),
      vacationDays:
        employee.vacationDays === null || employee.vacationDays === undefined
          ? ""
          : String(employee.vacationDays),
      notes: employee.notes || "",
      addressStreet: employee.addressStreet || "",
      addressNumber: employee.addressNumber || "",
      addressComplement: employee.addressComplement || "",
      addressNeighborhood: employee.addressNeighborhood || "",
      addressCity: employee.addressCity || "",
      addressState: employee.addressState || "",
      addressZipCode: employee.addressZipCode || "",
      emergencyContactName: employee.emergencyContactName || "",
      emergencyContactPhone: employee.emergencyContactPhone || "",
      status: employee.status || "ACTIVE",
      userId: employee.user?.id ? String(employee.user.id) : "",
      vacationDelegateUserIds: Array.isArray(employee.vacationDelegates)
        ? employee.vacationDelegates.map((delegate) => String(delegate.id))
        : [],
    });
    setFormError("");
    setModalOpen(true);
    if (canManage) {
      loadAssignableUsers();
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    if (name === "userId") {
      setForm((prev) => {
        if (!value) {
          return { ...prev, userId: "" };
        }
        const selectedUser = availableUsers.find((systemUser) => String(systemUser.id) === value);
        if (!selectedUser) {
          return { ...prev, userId: value };
        }

        return {
          ...prev,
          userId: value,
          fullName: selectedUser.name || prev.fullName,
          email: selectedUser.email || prev.email,
          birthDate: selectedUser.birthDate
            ? normalizeDateInput(selectedUser.birthDate)
            : prev.birthDate,
          department: selectedUser.sectorName || prev.department,
        };
      });
      return;
    }
    if (name === "status" && value !== "VACATION") {
      setForm((prev) => ({ ...prev, status: value, vacationDelegateUserIds: [] }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelegateToggle = (userId) => {
    setForm((prev) => {
      const id = String(userId);
      const current = new Set(prev.vacationDelegateUserIds || []);
      if (current.has(id)) {
        current.delete(id);
      } else {
        current.add(id);
      }
      return { ...prev, vacationDelegateUserIds: Array.from(current) };
    });
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setFilters({ ...filterForm });
  };

  const handleResetFilters = () => {
    setFilterForm({ ...defaultFilters });
    setFilters({ ...defaultFilters });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) {
      return;
    }
    setSaving(true);
    setFormError("");

    const payload = {
      registrationNumber: form.registrationNumber || null,
      fullName: form.fullName,
      socialName: form.socialName || null,
      email: form.email || null,
      phone: form.phone || null,
      cpf: form.cpf || null,
      rg: form.rg || null,
      birthDate: form.birthDate || null,
      gender: form.gender || null,
      maritalStatus: form.maritalStatus || null,
      department: form.department || null,
      position: form.position || null,
      employmentType: form.employmentType || null,
      hireDate: form.hireDate || null,
      terminationDate: form.terminationDate || null,
      workShift: form.workShift || null,
      salary: form.salary || null,
      vacationDays: form.vacationDays || null,
      notes: form.notes || null,
      addressStreet: form.addressStreet || null,
      addressNumber: form.addressNumber || null,
      addressComplement: form.addressComplement || null,
      addressNeighborhood: form.addressNeighborhood || null,
      addressCity: form.addressCity || null,
      addressState: form.addressState || null,
      addressZipCode: form.addressZipCode || null,
      emergencyContactName: form.emergencyContactName || null,
      emergencyContactPhone: form.emergencyContactPhone || null,
      status: form.status || "ACTIVE",
      userId: form.userId || null,
      vacationDelegateUserIds: Array.isArray(form.vacationDelegateUserIds)
        ? form.vacationDelegateUserIds
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        : [],
    };

    try {
      if (editingId) {
        await HumanResourcesService.updateEmployee(editingId, payload);
      } else {
        await HumanResourcesService.createEmployee(payload);
      }
      closeModal();
      await loadEmployees();
      await loadOverview();
      await loadVacationControl();
    } catch (err) {
      const message = err?.response?.data?.error || "Não foi possível salvar o colaborador.";
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleHistorySubmit = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setHistorySaving(true);
    setHistoryError("");
    try {
      const payload = {
        employeeId: historyForm.employeeId || null,
        type: historyForm.type,
        startDate: historyForm.startDate || null,
        endDate: historyForm.endDate || null,
        days: historyForm.days || null,
        notes: historyForm.notes || null,
      };
      if (historyForm.id) {
        await HumanResourcesService.updateEmployeeHistory(historyForm.id, payload);
      } else {
        await HumanResourcesService.createEmployeeHistory(payload);
      }
      closeHistoryModal();
      await loadEmployees();
      await loadOverview();
      await loadVacationControl();
      await loadEmployeeHistory();
    } catch (err) {
      setHistoryError(err?.response?.data?.error || "Não foi possível cadastrar o histórico.");
    } finally {
      setHistorySaving(false);
    }
  };

  const handleEditHistory = (entry) => {
    setHistoryError("");
    setHistoryEmployeeSearch(entry.employeeName || "");
    setHistoryForm({
      id: entry.id,
      employeeId: String(entry.employeeId || ""),
      type: entry.type || "VACATION",
      startDate: normalizeDateInput(entry.startDate) || "",
      endDate: normalizeDateInput(entry.endDate) || "",
      days: entry.days ? String(entry.days) : "",
      notes: entry.notes || "",
    });
    setHistoryModalOpen(true);
    loadActiveEmployeesForHistory();
  };

  const handleDeleteHistory = async (entry) => {
    if (!canManage) return;
    const confirmed = window.confirm("Deseja excluir este histórico?");
    if (!confirmed) {
      return;
    }
    try {
      await HumanResourcesService.deleteEmployeeHistory(entry.id);
      await loadEmployeeHistory();
      await loadVacationControl();
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível remover o histórico.");
    }
  };

  const getEmployeeForUpdate = useCallback(
    async (employeeId) => {
      const fullEmployee = await HumanResourcesService.getEmployeeById(employeeId);
      if (!fullEmployee || !fullEmployee.id) {
        throw new Error("Colaborador não encontrado.");
      }
      return fullEmployee;
    },
    []
  );

  const openVacationEditModal = (entry = null) => {
    setVacationEditError("");
    setVacationEditForm({
      employeeId: entry ? String(entry.employeeId) : "",
      vacationDays:
        entry && entry.availableDays !== null && entry.availableDays !== undefined
          ? String(entry.availableDays)
          : "",
      acquisitionPeriodStart: normalizeDateInput(entry?.acquisitionPeriodStart) || "",
      acquisitionPeriodEnd: normalizeDateInput(entry?.acquisitionPeriodEnd) || "",
    });
    setVacationEditModalOpen(true);
  };

  const closeVacationEditModal = () => {
    if (vacationEditSaving) return;
    setVacationEditModalOpen(false);
    setVacationEditError("");
  };

  const handleVacationControlSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setVacationEditSaving(true);
    setVacationEditError("");
    try {
      if (!vacationEditForm.employeeId) {
        throw new Error("Selecione um colaborador.");
      }
      if (!vacationEditForm.acquisitionPeriodStart || !vacationEditForm.acquisitionPeriodEnd) {
        throw new Error("Informe o período aquisitivo completo.");
      }
      const employee = await getEmployeeForUpdate(Number(vacationEditForm.employeeId));
      await HumanResourcesService.updateEmployee(employee.id, {
        ...employee,
        hireDate: vacationEditForm.acquisitionPeriodStart,
        vacationDays: vacationEditForm.vacationDays || null,
      });
      closeVacationEditModal();
      await loadEmployees();
      await loadVacationControl();
      await loadOverview();
    } catch (err) {
      setVacationEditError(err?.response?.data?.error || err.message || "Não foi possível atualizar férias.");
    } finally {
      setVacationEditSaving(false);
    }
  };

  const handleDeleteAcquisitionPeriod = async (entry) => {
    if (!canManage) return;
    const confirmed = window.confirm("Deseja excluir o período aquisitivo deste colaborador?");
    if (!confirmed) return;
    try {
      const employee = await getEmployeeForUpdate(entry.employeeId);
      await HumanResourcesService.updateEmployee(employee.id, {
        ...employee,
        hireDate: null,
      });
      await loadVacationControl();
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível excluir o período aquisitivo.");
    }
  };

  const handleDelete = async (employee) => {
    if (!canManage) return;
    const confirmed = window.confirm(
      `Deseja remover o colaborador ${employee.fullName}? Essa ação não poderá ser desfeita.`
    );
    if (!confirmed) {
      return;
    }

    try {
      await HumanResourcesService.deleteEmployee(employee.id);
      await loadEmployees();
      await loadOverview();
      await loadVacationControl();
  } catch (err) {
      const message = err?.response?.data?.error || "Não foi possível remover o colaborador.";
      setError(message);
    }
  };

  const handleDownloadImportTemplate = useCallback(() => {
    try {
      const isEmployeesTemplate = importType === "EMPLOYEES";
      const columns = isEmployeesTemplate ? EMPLOYEE_IMPORT_COLUMNS : HISTORY_IMPORT_COLUMNS;
      const templateRows = [columns.reduce((acc, column) => ({ ...acc, [getImportColumnLabel(column)]: "" }), {})];
      const worksheet = XLSXUtils.json_to_sheet(templateRows, {
        header: columns.map((column) => getImportColumnLabel(column)),
        skipHeader: false,
      });
      worksheet["!cols"] = columns.map((column) => ({ wch: Math.max(column.label.length + 4, 24) }));

      const instructionsSheet = XLSXUtils.json_to_sheet(
        columns.map((column) => ({
          Coluna: getImportColumnLabel(column),
          Obrigatorio: column.required ? "Sim" : "Não",
          Orientacao: column.instructions,
        }))
      );
      instructionsSheet["!cols"] = [{ wch: 34 }, { wch: 12 }, { wch: 80 }];

      const optionsRows = isEmployeesTemplate
        ? [
            { Tipo: "Status válidos", Opcoes: Object.values(STATUS_LABELS).join(" | ") },
            { Tipo: "Tipos de vínculo", Opcoes: EMPLOYMENT_TYPES.join(" | ") },
          ]
        : [{ Tipo: "Tipos de histórico", Opcoes: Object.values(HISTORY_TYPE_LABELS).join(" | ") }];
      const optionsSheet = XLSXUtils.json_to_sheet(optionsRows);
      optionsSheet["!cols"] = [{ wch: 30 }, { wch: 100 }];

      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, isEmployeesTemplate ? "Colaboradores" : "Historico");
      XLSXUtils.book_append_sheet(workbook, instructionsSheet, "Instrucoes");
      XLSXUtils.book_append_sheet(workbook, optionsSheet, "Opcoes");
      writeXLSXFile(
        workbook,
        isEmployeesTemplate ? "template-importacao-colaboradores.xlsx" : "template-importacao-historico-ferias.xlsx",
        { bookType: "xlsx" }
      );
      setImportSummary("Template baixado com sucesso.");
    } catch (err) {
      setImportError("Não foi possível gerar o template de importação.");
    }
  }, [importType]);

  const normalizeStatus = (value) => {
    if (!value) return "";
    const normalized = normalizeImportText(value);
    const entries = Object.entries(STATUS_LABELS);
    const match = entries.find(([key, label]) => {
      return normalizeImportText(key) === normalized || normalizeImportText(label) === normalized;
    });
    return match ? match[0] : "";
  };

  const normalizeHistoryType = (value) => {
    if (!value) return "";
    const normalized = normalizeImportText(value);
    const entries = Object.entries(HISTORY_TYPE_LABELS);
    const match = entries.find(([key, label]) => {
      return normalizeImportText(key) === normalized || normalizeImportText(label) === normalized;
    });
    return match ? match[0] : "";
  };

  const handleImportFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      setImportError("");
      setImportSummary("");
      setImportingData(true);

      try {
        const buffer = await file.arrayBuffer();
        const workbook = readXLSX(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
          throw new Error("Planilha inválida: aba de dados não encontrada.");
        }

        const rows = XLSXUtils.sheet_to_json(worksheet, { defval: "" });
        if (!rows.length) {
          throw new Error("A planilha está vazia.");
        }

        const toPayload = (columns, row) => {
          const normalizedRow = {};
          Object.entries(row).forEach(([header, value]) => {
            normalizedRow[normalizeImportHeader(header)] = value;
          });
          return columns.reduce((acc, column) => {
            const columnHeader = normalizeImportHeader(column.label);
            const requiredHeader = normalizeImportHeader(getImportColumnLabel(column));
            acc[column.key] = coerceImportValue(
              normalizedRow[requiredHeader] ?? normalizedRow[columnHeader]
            );
            return acc;
          }, {});
        };

        let successCount = 0;
        const errors = [];

        if (importType === "EMPLOYEES") {
          for (let index = 0; index < rows.length; index += 1) {
            const payload = toPayload(EMPLOYEE_IMPORT_COLUMNS, rows[index]);
            if (!payload.fullName) {
              errors.push(`Linha ${index + 2}: "Nome completo" é obrigatório.`);
              continue;
            }
            const normalizedStatus = normalizeStatus(payload.status);
            if (!normalizedStatus) {
              errors.push(`Linha ${index + 2}: status inválido.`);
              continue;
            }
            await HumanResourcesService.createEmployee({
              registrationNumber: payload.registrationNumber || null,
              fullName: payload.fullName,
              email: payload.email || null,
              phone: payload.phone || null,
              department: payload.department || null,
              position: payload.position || null,
              employmentType: payload.employmentType || null,
              hireDate: payload.hireDate || null,
              salary: payload.salary || null,
              vacationDays: payload.vacationDays || null,
              status: normalizedStatus,
              notes: payload.notes || null,
            });
            successCount += 1;
          }
        } else {
          const employeeList = await HumanResourcesService.listEmployees();
          const employeeByRegistration = new Map(
            employeeList
              .filter((employee) => employee.registrationNumber)
              .map((employee) => [normalizeImportText(employee.registrationNumber), employee])
          );
          const employeeByEmail = new Map(
            employeeList
              .filter((employee) => employee.email)
              .map((employee) => [normalizeImportText(employee.email), employee])
          );
          const employeeByName = new Map(
            employeeList
              .filter((employee) => employee.fullName)
              .map((employee) => [normalizeImportText(employee.fullName), employee])
          );

          for (let index = 0; index < rows.length; index += 1) {
            const payload = toPayload(HISTORY_IMPORT_COLUMNS, rows[index]);
            const employee =
              employeeByRegistration.get(normalizeImportText(payload.registrationNumber)) ||
              employeeByEmail.get(normalizeImportText(payload.employeeEmail)) ||
              employeeByName.get(normalizeImportText(payload.employeeName));
            if (!employee?.id) {
              errors.push(`Linha ${index + 2}: colaborador não localizado.`);
              continue;
            }

            const normalizedType = normalizeHistoryType(payload.type);
            if (!normalizedType) {
              errors.push(`Linha ${index + 2}: tipo de histórico inválido.`);
              continue;
            }
            if (normalizedType === "VACATION" && !payload.days) {
              errors.push(`Linha ${index + 2}: informe "Dias" para férias.`);
              continue;
            }

            await HumanResourcesService.createEmployeeHistory({
              employeeId: employee.id,
              type: normalizedType,
              startDate: payload.startDate || null,
              endDate: payload.endDate || null,
              days: payload.days || null,
              notes: payload.notes || null,
            });
            successCount += 1;
          }
        }

        await loadEmployees();
        await loadOverview();
        await loadVacationControl();
        await loadEmployeeHistory();

        if (errors.length) {
          setImportError(
            `${successCount} registro(s) importado(s). ${errors.length} linha(s) com erro: ${errors
              .slice(0, 4)
              .join(" | ")}${errors.length > 4 ? " ..." : ""}`
          );
        } else {
          setImportSummary(`${successCount} registro(s) importado(s) com sucesso.`);
        }
      } catch (err) {
        setImportError(err?.message || "Não foi possível importar a planilha.");
      } finally {
        setImportingData(false);
      }
    },
    [importType, loadEmployeeHistory, loadEmployees, loadOverview, loadVacationControl]
  );

  const summaryData = overview?.summary ?? defaultOverview.summary;
  const headcountByDepartment = overview?.headcountByDepartment ?? [];
  const employmentTypeDistribution = overview?.employmentTypeDistribution ?? [];
  const recentHires = overview?.recentHires ?? [];
  const upcomingBirthdays = overview?.upcomingBirthdays ?? [];
  const withoutSystemAccessList = overview?.withoutSystemAccess ?? [];
  const vacationSchedule = overview?.vacationSchedule ?? [];
  const vacationControlList = vacationControl ?? [];

  const summaryHighlights = useMemo(
    () => [
      {
        id: "total",
        label: "Colaboradores",
        value: overviewLoading ? "…" : summaryData.total,
        icon: FaUsers,
        hint: "Total cadastrado no módulo de RH.",
        cardClassName:
          "border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-indigo-100/70 hover:border-indigo-300",
        labelClassName: "text-indigo-600",
        valueClassName: "text-indigo-950",
        hintClassName: "text-indigo-700/80",
        iconClassName: "bg-indigo-100 text-indigo-600 group-hover:bg-indigo-200 group-hover:text-indigo-700",
      },
      {
        id: "active",
        label: "Ativos",
        value: overviewLoading ? "…" : summaryData.active,
        icon: FaUserTie,
        hint: "Colaboradores atualmente ativos.",
        cardClassName:
          "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-100/70 hover:border-emerald-300",
        labelClassName: "text-emerald-700",
        valueClassName: "text-emerald-950",
        hintClassName: "text-emerald-700/80",
        iconClassName:
          "bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200 group-hover:text-emerald-700",
      },
      {
        id: "vacation",
        label: "Em férias",
        value: overviewLoading ? "…" : summaryData.vacation,
        icon: FaUmbrellaBeach,
        hint: "Colaboradores afastados por férias.",
        cardClassName:
          "border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-100/70 hover:border-sky-300",
        labelClassName: "text-sky-700",
        valueClassName: "text-sky-950",
        hintClassName: "text-sky-700/80",
        iconClassName: "bg-sky-100 text-sky-600 group-hover:bg-sky-200 group-hover:text-sky-700",
      },
      {
        id: "onLeave",
        label: "Licenças",
        value: overviewLoading ? "…" : summaryData.onLeave,
        icon: FaUserClock,
        hint: "Licenças médicas ou não remuneradas.",
        cardClassName:
          "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-100/70 hover:border-amber-300",
        labelClassName: "text-amber-700",
        valueClassName: "text-amber-950",
        hintClassName: "text-amber-700/80",
        iconClassName:
          "bg-amber-100 text-amber-600 group-hover:bg-amber-200 group-hover:text-amber-700",
      },
      {
        id: "withoutAccess",
        label: "Sem acesso",
        value: overviewLoading ? "…" : summaryData.withoutSystemAccess,
        icon: FaBan,
        hint: "Não possuem credenciais do sistema.",
        cardClassName:
          "border-rose-200 bg-gradient-to-br from-rose-50 via-white to-rose-100/70 hover:border-rose-300",
        labelClassName: "text-rose-700",
        valueClassName: "text-rose-950",
        hintClassName: "text-rose-700/80",
        iconClassName: "bg-rose-100 text-rose-600 group-hover:bg-rose-200 group-hover:text-rose-700",
      },
      {
        id: "withAccess",
        label: "Com acesso",
        value: overviewLoading ? "…" : summaryData.withSystemAccess,
        icon: FaIdBadge,
        hint: "Usuários com login ativo.",
        cardClassName:
          "border-violet-200 bg-gradient-to-br from-violet-50 via-white to-violet-100/70 hover:border-violet-300",
        labelClassName: "text-violet-700",
        valueClassName: "text-violet-950",
        hintClassName: "text-violet-700/80",
        iconClassName:
          "bg-violet-100 text-violet-600 group-hover:bg-violet-200 group-hover:text-violet-700",
      },
    ],
    [overviewLoading, summaryData]
  );

  const vacationControlSection = (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="flex flex-col gap-1 border-b border-slate-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
          <FaCalendarCheck className="text-emerald-600" />
          Controle de férias
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-slate-400">
            Períodos aquisitivos, vencimentos de gozo e saldos do colaborador.
          </p>
          {canManage && (
            <button
              type="button"
              onClick={() => openVacationEditModal()}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-xs font-semibold text-emerald-700"
            >
              <FaUserPlus /> Adicionar saldo/período
            </button>
          )}
        </div>
      </header>
      <div className="mt-4 space-y-3">
        {vacationControlError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {vacationControlError}
          </div>
        )}

        {!vacationControlError && (
          <div className="overflow-x-auto">
            {vacationControlLoading ? (
              <p className="text-sm text-slate-500">Carregando painel de férias...</p>
            ) : vacationControlList.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nenhum colaborador cadastrado no controle de férias até o momento.
              </p>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Colaborador</th>
                    <th className="px-4 py-3">Período aquisitivo</th>
                    <th className="px-4 py-3">Prazo de concessão</th>
                    <th className="px-4 py-3">Próximo período</th>
                    <th className="px-4 py-3">Dias</th>
                    {canManage && <th className="px-4 py-3 text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vacationControlList.map((entry) => (
                    <tr key={entry.employeeId} className="align-top text-sm text-slate-700">
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-slate-900">{entry.employeeName}</span>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                STATUS_STYLES[entry.status] || "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {STATUS_LABELS[entry.status] || entry.status}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {entry.department
                              ? `${entry.department}${entry.position ? ` • ${entry.position}` : ""}`
                              : entry.position || "Sem departamento"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800">
                            {formatDateRange(entry.acquisitionPeriodStart, entry.acquisitionPeriodEnd)}
                          </span>
                          {entry.hireDate && (
                            <span className="text-xs text-slate-500">
                              Admissão: {formatDateOnly(entry.hireDate)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800">
                            {entry.concessionPeriodEnd
                              ? `Vence em ${formatDateOnly(entry.concessionPeriodEnd)}`
                              : "Prazo não calculado"}
                          </span>
                          {entry.daysUntilConcessionEnds !== null &&
                            entry.daysUntilConcessionEnds !== undefined && (
                              <span className="text-xs text-amber-700">
                                {entry.daysUntilConcessionEnds === 0
                                  ? "Prazo encerra hoje"
                                  : `Faltam ${formatDaysLabel(entry.daysUntilConcessionEnds)}`}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800">
                            {entry.nextAcquisitionEnd
                              ? formatDateOnly(entry.nextAcquisitionEnd)
                              : "Data não calculada"}
                          </span>
                          {entry.daysUntilNextAcquisitionEnd !== null &&
                            entry.daysUntilNextAcquisitionEnd !== undefined && (
                              <span className="text-xs text-slate-500">
                                Próximo período em {formatDaysLabel(entry.daysUntilNextAcquisitionEnd)}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                            Direito: {formatDaysLabel(entry.entitledDays)}
                          </span>
                          <span className="rounded-full bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-700">
                            Usados: {formatDaysLabel(entry.usedDays)}
                          </span>
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                            Saldo: {formatDaysLabel(entry.availableDays)}
                          </span>
                        </div>
                      </td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openVacationEditModal(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600"
                            >
                              <FaEdit /> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteAcquisitionPeriod(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-medium text-rose-600"
                            >
                              <FaTrash /> Excluir período
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </section>
  );
  const columnCount = canManage ? 8 : 7;

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-800">
            <FaIdBadge className="text-slate-500" />
            Recursos Humanos
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Cadastre colaboradores, acompanhe vínculos e mantenha os dados de RH atualizados.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FaSync className="text-slate-400" /> Atualizar
          </button>
        </div>
      </header>

      <nav className="flex flex-wrap gap-2">
        {TAB_ITEMS.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-100 ${
                isActive
                  ? "bg-slate-900 text-white shadow"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              <TabIcon />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === "overview" && (
        <div className="space-y-8">

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Ações rápidas
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Cadastre colaboradores e exporte informações em planilhas ou PDFs prontos para uso.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canManage && (
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                <FaUserPlus /> Cadastrar colaborador
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={openHistoryModal}
                className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-100"
              >
                <FaClipboardList /> Cadastrar histórico
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={openImportModal}
                className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-100"
              >
                <FaFileImport /> Importar Excel
              </button>
            )}
            <button
              type="button"
              onClick={handleExportCurrentFilters}
              disabled={exporting || loading}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaFileExcel /> {exporting ? "Gerando CSV..." : "Exportar CSV"}
            </button>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf || loading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaFilePdf /> {downloadingPdf ? "Gerando PDF..." : "Exportar PDF"}
            </button>
            <button
              type="button"
              onClick={openReportModal}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaClipboardList /> Relatórios avançados
            </button>
          </div>
        </div>
      </section>

      {overviewError && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          {overviewError}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {summaryHighlights.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.id}
              className={`group rounded-2xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${card.cardClassName}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${card.labelClassName}`}>
                    {card.label}
                  </p>
                  <p className={`mt-3 text-3xl font-semibold ${card.valueClassName}`}>{card.value}</p>
                </div>
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition ${card.iconClassName}`}
                >
                  <Icon />
                </div>
              </div>
              <p className={`mt-3 text-xs ${card.hintClassName}`}>{card.hint}</p>
            </div>
          );
        })}
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
              <FaBuilding className="text-slate-400" />
              Distribuição por departamento
            </h3>
            {!overviewLoading && (
              <span className="text-xs text-slate-400">Atualizado automaticamente</span>
            )}
          </header>
          <div className="mt-4 space-y-3">
            {overviewLoading ? (
              <p className="text-sm text-slate-500">Carregando indicadores...</p>
            ) : headcountByDepartment.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum dado cadastrado até o momento.</p>
            ) : (
              headcountByDepartment.map((item) => (
                <div
                  key={item.department}
                  className="flex items-center justify-between text-sm text-slate-600"
                >
                  <span>{item.department}</span>
                  <span className="font-semibold text-slate-900">{item.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <FaChartPie className="text-slate-400" />
            Distribuição por vínculo
          </header>
          <div className="mt-4 space-y-3">
            {overviewLoading ? (
              <p className="text-sm text-slate-500">Carregando indicadores...</p>
            ) : employmentTypeDistribution.length === 0 ? (
              <p className="text-sm text-slate-500">Cadastre colaboradores para visualizar esta distribuição.</p>
            ) : (
              employmentTypeDistribution.map((item, index) => (
                <div
                  key={`${item.employmentType || "sem-informacao"}-${index}`}
                  className="flex items-center justify-between text-sm text-slate-600"
                >
                  <span>{item.employmentType || "Não informado"}</span>
                  <span className="font-semibold text-slate-900">{item.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <FaClipboardList className="text-slate-400" />
            Novas admissões
          </header>
          <div className="mt-4 space-y-3">
            {overviewLoading ? (
              <p className="text-sm text-slate-500">Carregando indicadores...</p>
            ) : recentHires.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma admissão registrada nos últimos cadastros.</p>
            ) : (
              recentHires.map((hire) => (
                <div
                  key={hire.id}
                  className="flex flex-col rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-600"
                >
                  <span className="font-semibold text-slate-800">{hire.fullName}</span>
                  <span className="text-xs text-slate-500">
                    {hire.department
                      ? `${hire.department}${hire.position ? ` • ${hire.position}` : ""}`
                      : hire.position || "Sem departamento"}
                  </span>
                  {hire.hireDate && (
                    <span className="mt-1 text-xs text-slate-400">
                      Admissão: {formatDateOnly(hire.hireDate)}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <FaBirthdayCake className="text-rose-500" />
            Aniversariantes nos próximos 60 dias
          </header>
          <div className="mt-4 space-y-3">
            {overviewLoading ? (
              <p className="text-sm text-slate-500">Carregando indicadores...</p>
            ) : upcomingBirthdays.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum colaborador faz aniversário nos próximos dias.</p>
            ) : (
              upcomingBirthdays.map((birthday) => (
                <div
                  key={birthday.id}
                  className="flex items-center justify-between rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                >
                  <div>
                    <p className="font-semibold text-rose-800">{birthday.fullName}</p>
                    <p className="text-xs text-rose-600">
                      {birthday.department
                        ? `${birthday.department}${birthday.position ? ` • ${birthday.position}` : ""}`
                        : birthday.position || "Sem departamento"}
                    </p>
                  </div>
                  <div className="text-right text-xs text-rose-600">
                    <div>{formatDateOnly(birthday.birthDate)}</div>
                    <div className="font-semibold">
                      {birthday.daysUntilBirthday === 0
                        ? "Hoje!"
                        : `${birthday.daysUntilBirthday} dia${birthday.daysUntilBirthday === 1 ? "" : "s"}`}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            <FaBan className="text-slate-500" />
            Colaboradores sem acesso ao sistema
          </header>
          <div className="mt-4 space-y-3">
            {overviewLoading ? (
              <p className="text-sm text-slate-500">Carregando indicadores...</p>
            ) : withoutSystemAccessList.length === 0 ? (
              <p className="text-sm text-slate-500">Todos os colaboradores cadastrados possuem acesso ao sistema.</p>
            ) : (
              withoutSystemAccessList.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-600"
                >
                  <div>
                    <span className="block font-semibold text-slate-800">{item.fullName}</span>
                    <span className="block text-xs text-slate-500">
                      {item.department
                        ? `${item.department}${item.position ? ` • ${item.position}` : ""}`
                        : item.position || "Sem departamento"}
                    </span>
                  </div>
                  {item.hireDate && (
                    <span className="text-xs text-slate-400">
                      Admissão: {formatDateOnly(item.hireDate)}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <header className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          <FaUmbrellaBeach className="text-sky-600" />
          Colaboradores em férias ou licença
        </header>
        <div className="mt-4 space-y-3">
          {overviewLoading ? (
            <p className="text-sm text-slate-500">Carregando indicadores...</p>
          ) : vacationSchedule.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum colaborador está em férias ou licença neste momento.</p>
          ) : (
            vacationSchedule.map((vacation) => (
              <div
                key={vacation.id}
                className="flex flex-col gap-1 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-sm text-sky-700"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sky-800">{vacation.fullName}</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-sky-600">
                    {STATUS_LABELS[vacation.status] || vacation.status}
                  </span>
                </div>
                <span className="text-xs text-sky-600">
                  {vacation.department
                    ? `${vacation.department}${vacation.position ? ` • ${vacation.position}` : ""}`
                    : vacation.position || "Sem departamento"}
                </span>
                {vacation.notes && (
                  <span className="text-xs text-sky-700">Observações: {vacation.notes}</span>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      <form
        onSubmit={handleApplyFilters}
        className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-5"
      >
        <div className="md:col-span-2">
          <label htmlFor="hr-filter-search" className="mb-1 block text-sm font-medium text-slate-600">
            Busca rápida
          </label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id="hr-filter-search"
                name="search"
                type="search"
                placeholder="Nome, CPF, e-mail ou matrícula"
                value={filterForm.search}
                onChange={(event) =>
                  setFilterForm((prev) => ({ ...prev, search: event.target.value }))
                }
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              Aplicar
            </button>
          </div>
        </div>
        <div>
          <label htmlFor="hr-filter-status" className="mb-1 block text-sm font-medium text-slate-600">
            Status
          </label>
          <select
            id="hr-filter-status"
            value={filterForm.status}
            onChange={(event) =>
              setFilterForm((prev) => ({ ...prev, status: event.target.value }))
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="hr-filter-employment" className="mb-1 block text-sm font-medium text-slate-600">
            Tipo de vínculo
          </label>
          <select
            id="hr-filter-employment"
            value={filterForm.employmentType}
            onChange={(event) =>
              setFilterForm((prev) => ({ ...prev, employmentType: event.target.value }))
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          >
            <option value="ALL">Todos</option>
            {EMPLOYMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="hr-filter-department" className="mb-1 block text-sm font-medium text-slate-600">
            Departamento
          </label>
          <input
            id="hr-filter-department"
            type="text"
            list="hr-department-suggestions"
            placeholder="Todos"
            value={filterForm.department}
            onChange={(event) =>
              setFilterForm((prev) => ({ ...prev, department: event.target.value }))
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />
          <datalist id="hr-department-suggestions">
            {departmentSuggestions.map((department) => (
              <option key={department} value={department} />
            ))}
          </datalist>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            Limpar filtros
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Colaborador
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Departamento / Cargo
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Vínculo
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Admissão
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Salário
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                Usuário do sistema
              </th>
              {canManage && (
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Ações
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading ? (
              <tr>
                <td colSpan={columnCount} className="px-6 py-12 text-center text-sm text-slate-500">
                  Carregando colaboradores...
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="px-6 py-12 text-center text-sm text-slate-500">
                  Nenhum colaborador encontrado com os filtros selecionados.
                </td>
              </tr>
            ) : (
              employees.map((employee) => (
                <tr key={employee.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900">
                        {employee.fullName}
                      </span>
                      <span className="mt-1 text-xs text-slate-500">
                        {employee.registrationNumber
                          ? `Matrícula ${employee.registrationNumber}`
                          : "Sem matrícula"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-700">
                      {formatDepartment(employee.department, employee.position)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {formatEmploymentType(employee.employmentType)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        STATUS_STYLES[employee.status] || "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {STATUS_LABELS[employee.status] || employee.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {employee.hireDate ? formatDateOnly(employee.hireDate) : "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {formatCurrency(employee.salary)}
                  </td>
                  <td className="px-6 py-4">
                    {employee.user ? (
                      <div className="flex flex-col text-sm text-slate-700">
                        <span className="font-medium">{employee.user.name}</span>
                        <span className="mt-0.5 text-xs text-slate-500">{employee.user.email}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Sem acesso</span>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => openEditModal(employee)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-emerald-300 hover:text-emerald-700"
                        >
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(employee)}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-600 shadow-sm transition hover:border-rose-300 hover:text-rose-700"
                        >
                          <FaTrash /> Excluir
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

        </div>
      )}

      {activeTab === "vacationControl" && (
        <div className="space-y-8">{vacationControlSection}</div>
      )}

      {activeTab === "employeeHistory" && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <header className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Histórico do colaborador
            </h2>
          </header>
          <div className="mt-4 overflow-x-auto">
            {historyLoading ? (
              <p className="text-sm text-slate-500">Carregando histórico...</p>
            ) : employeeHistory.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum histórico cadastrado até o momento.</p>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Colaborador</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Período</th>
                    <th className="px-4 py-3">Dias</th>
                    <th className="px-4 py-3">Observações</th>
                    {canManage && <th className="px-4 py-3 text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {employeeHistory.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{entry.employeeName}</td>
                      <td className="px-4 py-3">{HISTORY_TYPE_LABELS[entry.type] || entry.type}</td>
                      <td className="px-4 py-3">{formatDateRange(entry.startDate, entry.endDate)}</td>
                      <td className="px-4 py-3">{entry.days ? `${entry.days} dias` : "—"}</td>
                      <td className="px-4 py-3">{entry.notes || "—"}</td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditHistory(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:border-slate-300"
                            >
                              <FaEdit /> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteHistory(entry)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-medium text-rose-600 hover:border-rose-300"
                            >
                              <FaTrash /> Excluir
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      <Modal open={reportModalOpen} onClose={closeReportModal} size="max-w-2xl">
        <div className="space-y-5">
          <header className="space-y-1">
            <h2 className="text-xl font-semibold text-slate-800">Relatórios avançados</h2>
            <p className="text-sm text-slate-500">
              Baixe planilhas (CSV) ou relatórios em PDF com os dados dos colaboradores utilizando filtros prontos.
            </p>
          </header>

          {reportError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {reportError}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => handleReportDownload({})}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaFileAlt /> Relatório completo
            </button>
            <button
              type="button"
              onClick={() => handleReportDownload({ status: "ACTIVE" })}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaUserTie /> Somente ativos
            </button>
            <button
              type="button"
              onClick={() => handleReportDownload({ status: "VACATION" })}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaUmbrellaBeach /> Férias
            </button>
            <button
              type="button"
              onClick={() => handleReportDownload({ status: "ON_LEAVE" })}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 shadow-sm transition hover:border-amber-300 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaUserClock /> Licença
            </button>
            <button
              type="button"
              onClick={() => handleReportDownload({ status: "TERMINATED" })}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrash /> Desligados
            </button>
            <button
              type="button"
              onClick={() => handleReportDownload(requestFilters)}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSearch /> Aplicar filtros atuais
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-600">Relatório em PDF</h3>
                <p className="text-xs text-slate-500">
                  Documento pronto para impressão com a identidade visual da empresa.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    downloadEmployeesPdf({}, { closeModal: true, onError: setReportError })
                  }
                  disabled={downloadingPdf}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaFilePdf /> {downloadingPdf ? "Gerando PDF..." : "PDF completo"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    downloadEmployeesPdf(requestFilters, {
                      closeModal: true,
                      onError: setReportError,
                    })
                  }
                  disabled={downloadingPdf}
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSearch /> {downloadingPdf ? "Gerando PDF..." : "PDF com filtros atuais"}
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Os relatórios CSV e PDF utilizam os mesmos filtros aplicados nesta tela.
          </p>
        </div>
      </Modal>

      <Modal open={historyModalOpen} onClose={closeHistoryModal} size="max-w-2xl">
        <form onSubmit={handleHistorySubmit} className="space-y-4">
          <header>
            <h2 className="text-xl font-semibold text-slate-800">
              {historyForm.id ? "Editar histórico" : "Cadastrar histórico"}
            </h2>
            <p className="text-sm text-slate-500">
              Registre licença, férias, ocorrência ou desligamento do colaborador.
            </p>
          </header>

          {historyError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {historyError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-600">Colaborador</label>
              <input
                type="text"
                value={historyEmployeeSearch}
                onChange={(event) => setHistoryEmployeeSearch(event.target.value)}
                placeholder="Filtrar colaborador..."
                className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
              <select
                value={historyForm.employeeId}
                onChange={(event) =>
                  setHistoryForm((prev) => ({ ...prev, employeeId: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              >
                <option value="">Selecione</option>
                {filteredActiveEmployeeOptions.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                Lista exibindo colaboradores ativos.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Tipo</label>
              <select
                value={historyForm.type}
                onChange={(event) =>
                  setHistoryForm((prev) => {
                    const nextType = event.target.value;
                    if (nextType !== "VACATION") {
                      return { ...prev, type: nextType };
                    }
                    return {
                      ...prev,
                      type: nextType,
                      endDate: calculateVacationEndDate(prev.startDate, prev.days),
                    };
                  })
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              >
                {HISTORY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Dias (férias)</label>
              <input
                type="number"
                min="1"
                value={historyForm.days}
                onChange={(event) => {
                  const nextDays = event.target.value;
                  setHistoryForm((prev) => ({
                    ...prev,
                    days: nextDays,
                    endDate:
                      prev.type === "VACATION"
                        ? calculateVacationEndDate(prev.startDate, nextDays)
                        : prev.endDate,
                  }));
                }}
                required={historyForm.type === "VACATION"}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Início</label>
              <input
                type="date"
                value={historyForm.startDate}
                onChange={(event) => {
                  const nextStartDate = event.target.value;
                  setHistoryForm((prev) => ({
                    ...prev,
                    startDate: nextStartDate,
                    endDate:
                      prev.type === "VACATION"
                        ? calculateVacationEndDate(nextStartDate, prev.days)
                        : prev.endDate,
                  }));
                }}
                required={historyForm.type === "VACATION"}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Fim</label>
              <input
                type="date"
                value={historyForm.endDate}
                onChange={(event) =>
                  setHistoryForm((prev) => ({ ...prev, endDate: event.target.value }))
                }
                readOnly={historyForm.type === "VACATION"}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-600">Observações</label>
              <textarea
                rows={3}
                value={historyForm.notes}
                onChange={(event) =>
                  setHistoryForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeHistoryModal}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={historySaving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-emerald-400"
            >
              {historySaving
                ? "Salvando..."
                : historyForm.id
                  ? "Salvar alterações"
                  : "Salvar histórico"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={importModalOpen} onClose={closeImportModal} size="max-w-2xl">
        <div className="space-y-4">
          <header>
            <h2 className="text-xl font-semibold text-slate-800">Importação em Excel</h2>
            <p className="text-sm text-slate-500">
              Baixe o modelo padrão e importe dados de cadastro de colaboradores ou histórico de férias.
            </p>
          </header>

          {importError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {importError}
            </div>
          )}
          {importSummary && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
              {importSummary}
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Tipo de importação</label>
            <select
              value={importType}
              onChange={(event) => setImportType(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
            >
              <option value="EMPLOYEES">Cadastro de colaboradores</option>
              <option value="HISTORY">Histórico de férias/licenças</option>
            </select>
          </div>

          <ol className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            <li><strong>1.</strong> Baixe e preencha o template.</li>
            <li><strong>2.</strong> Leia as abas <strong>Instrucoes</strong> e <strong>Opcoes</strong>.</li>
            <li><strong>3.</strong> Selecione o arquivo para iniciar a importação automática.</li>
          </ol>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleDownloadImportTemplate}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300"
            >
              <FaDownload /> Baixar template
            </button>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700">
              <FaFileImport /> {importingData ? "Importando..." : "Selecionar planilha"}
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                disabled={importingData}
                onChange={handleImportFileChange}
              />
            </label>
          </div>
        </div>
      </Modal>

      <Modal open={vacationEditModalOpen} onClose={closeVacationEditModal} size="max-w-2xl">
        <form onSubmit={handleVacationControlSubmit} className="space-y-4">
          <header>
            <h2 className="text-xl font-semibold text-slate-800">Ajustar controle de férias</h2>
            <p className="text-sm text-slate-500">
              Adicione saldo de dias e período aquisitivo para o colaborador.
            </p>
          </header>

          {vacationEditError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {vacationEditError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-600">Colaborador</label>
              <select
                value={vacationEditForm.employeeId}
                onChange={(event) =>
                  setVacationEditForm((prev) => ({ ...prev, employeeId: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              >
                <option value="">Selecione</option>
                {[...employees]
                  .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || "", "pt-BR"))
                  .map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Saldo de férias (dias)</label>
              <input
                type="number"
                min="0"
                value={vacationEditForm.vacationDays}
                onChange={(event) =>
                  setVacationEditForm((prev) => ({ ...prev, vacationDays: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div />
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Período aquisitivo (início)</label>
              <input
                type="date"
                value={vacationEditForm.acquisitionPeriodStart}
                onChange={(event) =>
                  setVacationEditForm((prev) => ({ ...prev, acquisitionPeriodStart: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Período aquisitivo (fim)</label>
              <input
                type="date"
                value={vacationEditForm.acquisitionPeriodEnd}
                onChange={(event) =>
                  setVacationEditForm((prev) => ({ ...prev, acquisitionPeriodEnd: event.target.value }))
                }
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeVacationEditModal}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={vacationEditSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-emerald-400"
            >
              {vacationEditSaving ? "Salvando..." : "Salvar controle"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={modalOpen} onClose={closeModal} size="max-w-5xl">
        <div className="space-y-6">
          <header className="space-y-1">
            <h2 className="text-xl font-semibold text-slate-800">
              {editingId ? "Editar colaborador" : "Novo colaborador"}
            </h2>
            <p className="text-sm text-slate-500">
              Preencha os dados cadastrais e defina se o colaborador terá acesso ao sistema.
            </p>
          </header>

          {formError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Dados pessoais
              </h3>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="md:col-span-2 xl:col-span-3">
                  <label htmlFor="hr-form-userId" className="mb-1 block text-sm font-medium text-slate-600">
                    Usuário associado
                  </label>
                  <select
                    id="hr-form-userId"
                    name="userId"
                    value={form.userId}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">Sem acesso ao sistema</option>
                    {availableUsers.map((systemUser) => (
                      <option
                        key={systemUser.id}
                        value={String(systemUser.id)}
                        disabled={
                          systemUser.employeeId &&
                          systemUser.employeeId !== editingId
                        }
                      >
                        {systemUser.name} — {systemUser.email}
                        {systemUser.sectorName ? ` • ${systemUser.sectorName}` : ""}
                        {systemUser.employeeId &&
                        systemUser.employeeId !== editingId
                          ? " (já vinculado)"
                          : ""}
                      </option>
                    ))}
                  </select>
                  {usersLoading && (
                    <p className="mt-1 text-xs text-slate-400">Carregando usuários...</p>
                  )}
                </div>
                <div className="xl:col-span-2">
                  <label htmlFor="hr-form-fullName" className="mb-1 block text-sm font-medium text-slate-600">
                    Nome completo
                  </label>
                  <input
                    id="hr-form-fullName"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleFormChange}
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-socialName" className="mb-1 block text-sm font-medium text-slate-600">
                    Nome social
                  </label>
                  <input
                    id="hr-form-socialName"
                    name="socialName"
                    value={form.socialName}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-email" className="mb-1 block text-sm font-medium text-slate-600">
                    E-mail corporativo
                  </label>
                  <div className="relative">
                    <FaEnvelope className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-phone" className="mb-1 block text-sm font-medium text-slate-600">
                    Telefone
                  </label>
                  <div className="relative">
                    <FaPhone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-cpf" className="mb-1 block text-sm font-medium text-slate-600">
                    CPF
                  </label>
                  <input
                    id="hr-form-cpf"
                    name="cpf"
                    value={form.cpf}
                    onChange={handleFormChange}
                    maxLength={14}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-rg" className="mb-1 block text-sm font-medium text-slate-600">
                    RG
                  </label>
                  <input
                    id="hr-form-rg"
                    name="rg"
                    value={form.rg}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-birthDate" className="mb-1 block text-sm font-medium text-slate-600">
                    Data de nascimento
                  </label>
                  <input
                    id="hr-form-birthDate"
                    name="birthDate"
                    type="date"
                    value={form.birthDate}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-gender" className="mb-1 block text-sm font-medium text-slate-600">
                    Gênero
                  </label>
                  <input
                    id="hr-form-gender"
                    name="gender"
                    value={form.gender}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-maritalStatus" className="mb-1 block text-sm font-medium text-slate-600">
                    Estado civil
                  </label>
                  <input
                    id="hr-form-maritalStatus"
                    name="maritalStatus"
                    value={form.maritalStatus}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Dados corporativos
              </h3>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label htmlFor="hr-form-registrationNumber" className="mb-1 block text-sm font-medium text-slate-600">
                    Matrícula
                  </label>
                  <div className="relative">
                    <FaIdBadge className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-registrationNumber"
                      name="registrationNumber"
                      value={form.registrationNumber}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-department" className="mb-1 block text-sm font-medium text-slate-600">
                    Departamento
                  </label>
                  <div className="relative">
                    <FaBuilding className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="hr-form-department"
                      name="department"
                      value={form.department}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="hr-form-position" className="mb-1 block text-sm font-medium text-slate-600">
                    Cargo
                  </label>
                  <input
                    id="hr-form-position"
                    name="position"
                    value={form.position}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-employmentType" className="mb-1 block text-sm font-medium text-slate-600">
                    Tipo de vínculo
                  </label>
                  <select
                    id="hr-form-employmentType"
                    name="employmentType"
                    value={form.employmentType}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">Selecione</option>
                    {EMPLOYMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="hr-form-hireDate" className="mb-1 block text-sm font-medium text-slate-600">
                    Data de admissão
                  </label>
                  <input
                    id="hr-form-hireDate"
                    name="hireDate"
                    type="date"
                    value={form.hireDate}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-terminationDate" className="mb-1 block text-sm font-medium text-slate-600">
                    Fim do contrato
                  </label>
                  <input
                    id="hr-form-terminationDate"
                    name="terminationDate"
                    type="date"
                    value={form.terminationDate}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-workShift" className="mb-1 block text-sm font-medium text-slate-600">
                    Jornada / turno
                  </label>
                  <input
                    id="hr-form-workShift"
                    name="workShift"
                    value={form.workShift}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-salary" className="mb-1 block text-sm font-medium text-slate-600">
                    Salário
                  </label>
                  <input
                    id="hr-form-salary"
                    name="salary"
                    type="text"
                    value={form.salary}
                    onChange={handleFormChange}
                    placeholder="0,00"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-vacationDays" className="mb-1 block text-sm font-medium text-slate-600">
                    Saldo de férias (dias)
                  </label>
                  <input
                    id="hr-form-vacationDays"
                    name="vacationDays"
                    type="number"
                    value={form.vacationDays}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-status" className="mb-1 block text-sm font-medium text-slate-600">
                    Status
                  </label>
                  <select
                    id="hr-form-status"
                    name="status"
                    value={form.status}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    {STATUS_OPTIONS.filter((option) => option.value !== "ALL").map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Endereço e emergência
              </h3>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="xl:col-span-2">
                  <label htmlFor="hr-form-addressStreet" className="mb-1 block text-sm font-medium text-slate-600">
                    Rua / logradouro
                  </label>
                  <input
                    id="hr-form-addressStreet"
                    name="addressStreet"
                    value={form.addressStreet}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressNumber" className="mb-1 block text-sm font-medium text-slate-600">
                    Número
                  </label>
                  <input
                    id="hr-form-addressNumber"
                    name="addressNumber"
                    value={form.addressNumber}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressComplement" className="mb-1 block text-sm font-medium text-slate-600">
                    Complemento
                  </label>
                  <input
                    id="hr-form-addressComplement"
                    name="addressComplement"
                    value={form.addressComplement}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressNeighborhood" className="mb-1 block text-sm font-medium text-slate-600">
                    Bairro
                  </label>
                  <input
                    id="hr-form-addressNeighborhood"
                    name="addressNeighborhood"
                    value={form.addressNeighborhood}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressCity" className="mb-1 block text-sm font-medium text-slate-600">
                    Cidade
                  </label>
                  <input
                    id="hr-form-addressCity"
                    name="addressCity"
                    value={form.addressCity}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressState" className="mb-1 block text-sm font-medium text-slate-600">
                    UF
                  </label>
                  <input
                    id="hr-form-addressState"
                    name="addressState"
                    value={form.addressState}
                    onChange={handleFormChange}
                    maxLength={2}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm uppercase text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-addressZipCode" className="mb-1 block text-sm font-medium text-slate-600">
                    CEP
                  </label>
                  <input
                    id="hr-form-addressZipCode"
                    name="addressZipCode"
                    value={form.addressZipCode}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-emergencyContactName" className="mb-1 block text-sm font-medium text-slate-600">
                    Contato de emergência
                  </label>
                  <input
                    id="hr-form-emergencyContactName"
                    name="emergencyContactName"
                    value={form.emergencyContactName}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label htmlFor="hr-form-emergencyContactPhone" className="mb-1 block text-sm font-medium text-slate-600">
                    Telefone de emergência
                  </label>
                  <input
                    id="hr-form-emergencyContactPhone"
                    name="emergencyContactPhone"
                    value={form.emergencyContactPhone}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              </div>
            </section>

            {canManage && (
              <section className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Acesso ao sistema
                </h3>
                <div>
                  <label htmlFor="hr-form-notes" className="mb-1 block text-sm font-medium text-slate-600">
                    Observações internas
                  </label>
                  <textarea
                    id="hr-form-notes"
                    name="notes"
                    rows={4}
                    value={form.notes}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                    <span className="text-sm font-medium text-slate-600">
                      Responsáveis durante férias
                    </span>
                    <span className="text-xs text-slate-400">
                      Disponível quando o status do colaborador for "Férias".
                    </span>
                  </div>
                  {form.status === "VACATION" ? (
                    delegateOptions.length ? (
                      <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3">
                        {delegateOptions.map((option) => {
                          const optionId = String(option.id);
                          const checked = form.vacationDelegateUserIds.includes(optionId);
                          return (
                            <label
                              key={option.id}
                              className="flex items-start gap-2 rounded-md border border-transparent px-2 py-1.5 text-sm text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                checked={checked}
                                onChange={() => handleDelegateToggle(option.id)}
                              />
                              <span>
                                <span className="font-medium">{option.name}</span>
                                <span className="block text-xs text-slate-500">{option.email}</span>
                                {option.sectorName && (
                                  <span className="block text-xs text-slate-400">{option.sectorName}</span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-slate-400">
                        Nenhum usuário disponível para atuar como substituto.
                      </p>
                    )
                  ) : (
                    <p className="mt-2 text-xs text-slate-400">
                      Defina o status como "Férias" para escolher responsáveis substitutos.
                    </p>
                  )}
                </div>
              </section>
            )}

            {!canManage && (
              <section>
                <label htmlFor="hr-form-notes" className="mb-1 block text-sm font-medium text-slate-600">
                  Observações internas
                </label>
                <textarea
                  id="hr-form-notes"
                  name="notes"
                  rows={4}
                  value={form.notes}
                  onChange={handleFormChange}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </section>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Cancelar
              </button>
              {canManage && (
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
                >
                  {saving ? "Salvando..." : "Salvar colaborador"}
                </button>
              )}
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
